import type { Message, StoredChat } from "@/types";

class ChatHistoryService {
  private readonly storageKey = "mdarj-voice-chat-history";

  private generateTitle(messages: Message[]): string {
    const firstUserMessage = messages.find((m) => m.role === "user");
    if (firstUserMessage) {
      const content = firstUserMessage.content.trim();
      if (content.length > 50) {
        return content.substring(0, 47) + "...";
      }
      return content;
    }
    return `Chat ${new Date().toLocaleDateString()}`;
  }

  saveChat(messages: Message[]): string {
    if (messages.length === 0) return "";

    const chats = this.getAllChats();
    const chatId = Date.now().toString();
    const title = this.generateTitle(messages);

    const newChat: StoredChat = {
      id: chatId,
      title,
      messages,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    chats.unshift(newChat);
    this.saveToStorage(chats);
    return chatId;
  }

  getAllChats(): StoredChat[] {
    try {
      if (typeof window === "undefined") return [];
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return [];

      const chats = JSON.parse(stored);
      return chats.map(
        (chat: {
          id: string;
          title: string;
          messages: Message[];
          createdAt: string;
          updatedAt: string;
        }) => ({
          ...chat,
          createdAt: new Date(chat.createdAt),
          updatedAt: new Date(chat.updatedAt),
          messages: chat.messages.map((m: Message & { timestamp: string }) => ({
            ...m,
            timestamp: new Date(m.timestamp),
          })),
        })
      );
    } catch (error) {
      console.error("Failed to load chat history:", error);
      return [];
    }
  }

  deleteChat(chatId: string): void {
    const chats = this.getAllChats();
    const filteredChats = chats.filter((chat) => chat.id !== chatId);
    this.saveToStorage(filteredChats);
  }

  getChat(chatId: string): StoredChat | null {
    const chats = this.getAllChats();
    return chats.find((chat) => chat.id === chatId) || null;
  }

  updateChat(chatId: string, messages: Message[]): void {
    const chats = this.getAllChats();
    const chatIndex = chats.findIndex((chat) => chat.id === chatId);

    if (chatIndex !== -1) {
      const title = this.generateTitle(messages);
      chats[chatIndex] = {
        ...chats[chatIndex],
        title,
        messages,
        updatedAt: new Date(),
      };
      this.saveToStorage(chats);
    }
  }

  clearAllHistory(): void {
    if (typeof window !== "undefined") {
      localStorage.removeItem(this.storageKey);
    }
  }

  private saveToStorage(chats: StoredChat[]): void {
    try {
      if (typeof window !== "undefined") {
        localStorage.setItem(this.storageKey, JSON.stringify(chats));
      }
    } catch (error) {
      console.error("Failed to save chat history:", error);
    }
  }
}

export const chatHistoryService = new ChatHistoryService();
